import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import logoImg from '../../assets/images/gowardhan_logo.png';
import { getModulesList, type ApiModule } from '../../api/modules';
import { getUserMenu } from '../../api/rbac';
import { hasPermission } from '../../hooks/useAuthorization';
import { useAppSelector } from '../../store/hooks';
import { routesConfig } from '../../routes';

interface SubMenuItem {
  name: string;
  path: string;
}

interface MenuItem {
  name: string;
  path?: string;
  icon: React.ReactNode;
  subMenu?: SubMenuItem[];
}

interface SidebarProps {
  currentPath: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Dynamically resolve route path for any module or submodule slug without static map objects
const resolveRoutePath = (slug: string, parentSlug?: string): string => {
  const rawSlug = (slug || '').trim().toLowerCase();
  const normSlug = rawSlug.replace(/_/g, '-');
  const normParentSlug = (parentSlug || '').trim().toLowerCase().replace(/_/g, '-');

  // Derive variations (strip '_list' or '-list' or '-catalog', and handle singular/plural)
  const baseSlug = normSlug.replace(/-(list|catalog)$/, '');
  const singularSlug = normSlug.endsWith('s') ? normSlug.slice(0, -1) : normSlug;
  const singularBaseSlug = baseSlug.endsWith('s') ? baseSlug.slice(0, -1) : baseSlug;

  const candidates = Array.from(
    new Set([
      normSlug,
      baseSlug,
      singularSlug,
      singularBaseSlug,
      `list-${normSlug}`,
      `list-${baseSlug}`,
      `list-${singularSlug}`,
      `list-${singularBaseSlug}`,
    ])
  );

  // Top level module route resolution
  if (!parentSlug) {
    for (const candidate of candidates) {
      const match = routesConfig.find(r => {
        const p = r.path.toLowerCase();
        return p === `/${candidate}` || p === `/${rawSlug}`;
      });
      if (match) return match.path;
    }
    return `/${normSlug}`;
  }

  // Submodule route resolution - 1. Exact match on candidate paths
  for (const candidate of candidates) {
    const match = routesConfig.find(r => {
      const p = r.path.toLowerCase();
      return p === `/${candidate}` || p === `/${normParentSlug}/${candidate}`;
    });
    if (match) return match.path;
  }

  // Submodule route resolution - 2. Partial match (path ending with candidate)
  for (const candidate of candidates) {
    const match = routesConfig.find(r => {
      const p = r.path.toLowerCase();
      return p.endsWith(`/${candidate}`) || p.endsWith(`/${candidate}s`);
    });
    if (match) return match.path;
  }

  return `/${normParentSlug}/${normSlug}`;
};

// Icon mapper function resolving API icon identifiers to SVG icons
const getModuleIcon = (iconName: string, slug?: string): React.ReactNode => {
  const normalizedIcon = (iconName || '').toLowerCase().trim();
  const normalizedSlug = (slug || '').toLowerCase().trim();

  if (normalizedIcon === 'dashboard' || normalizedSlug === 'dashboard') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    );
  }

  if (normalizedIcon === 'people' || normalizedIcon === 'users' || normalizedSlug === 'users') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 100 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  }

  if (normalizedIcon === 'wallet' || normalizedIcon === 'groups' || normalizedSlug.includes('wallet')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    );
  }

  if (normalizedIcon === 'distrubutor' || normalizedIcon === 'distributor' || normalizedSlug.includes('distrubutor') || normalizedSlug.includes('distributor')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
      </svg>
    );
  }

  if (normalizedIcon === 'category' || normalizedIcon === 'products' || normalizedSlug === 'products') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    );
  }

  if (normalizedIcon === 'local_offer' || normalizedIcon === 'scheme' || normalizedSlug === 'scheme') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
      </svg>
    );
  }

  if (
    normalizedIcon === 'insentive' ||
    normalizedIcon === 'incentive' ||
    normalizedSlug === 'insentive' ||
    normalizedSlug === 'incentive'
  ) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    );
  }

  if (normalizedIcon === 'shopping_cart' || normalizedIcon === 'orders' || normalizedSlug === 'orders') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    );
  }

  if (normalizedIcon === 'business' || normalizedIcon === 'organisation' || normalizedIcon === 'organization' || normalizedSlug === 'organisation' || normalizedSlug === 'organization') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0v-5a2 2 0 012-2h2a2 2 0 012 2v5m-4 0h4" />
      </svg>
    );
  }

  if (normalizedIcon === 'control_panel' || normalizedIcon === 'settings' || normalizedSlug === 'control_panel') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }

  if (normalizedIcon === 'inventory' || normalizedSlug === 'inventory') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    );
  }

  if (normalizedIcon === 'report' || normalizedSlug.includes('report')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
      </svg>
    );
  }

  if (normalizedIcon === 'notification' || normalizedSlug.includes('notification')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    );
  }

  if (normalizedIcon === 'security' || normalizedIcon === 'shield' || normalizedSlug.includes('roles') || normalizedSlug.includes('permission')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    );
  }

  if (normalizedIcon === 'sync' || normalizedIcon === 'refresh' || normalizedSlug.includes('sap') || normalizedSlug.includes('sync')) {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    );
  }

  // Fallback Icon for unmapped strings
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
};

const transformApiModules = (apiModules: ApiModule[]): MenuItem[] => {
  const sortedModules = [...apiModules].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

  return sortedModules.map((mod) => {
    const parentPath = resolveRoutePath(mod.slug);
    const hasSubModules = Array.isArray(mod.subModules) && mod.subModules.length > 0;

    const sortedSubModules = hasSubModules
      ? [...mod.subModules!].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      : [];

    const subMenu = hasSubModules
      ? sortedSubModules.map((sub) => ({
          name: sub.name.trim(),
          path: resolveRoutePath(sub.slug, mod.slug),
        }))
      : undefined;

    return {
      name: mod.name.trim(),
      path: parentPath,
      icon: getModuleIcon(mod.icon, mod.slug),
      subMenu,
    };
  });
};

export const Sidebar: React.FC<SidebarProps> = ({
  currentPath,
  isCollapsed,
  onToggleCollapse
}) => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [hoveredItem, setHoveredItem] = useState<MenuItem | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  const authPermissions = useAppSelector((state) => state.auth.permissions);
  const authRoles = useAppSelector((state) => state.auth.roles);

  const userPermissions = useMemo(() => authPermissions || [], [authPermissions]);
  const userRoles = useMemo(() => authRoles || [], [authRoles]);

  // Fetch modules dynamically from API
  useEffect(() => {
    let isMounted = true;
    const fetchModules = async () => {
      try {
        setLoading(true);
        setError(null);

        let modulesData: ApiModule[] = [];
        try {
          const userMenuRes = await getUserMenu();
          if (userMenuRes && userMenuRes.data && Array.isArray(userMenuRes.data)) {
            modulesData = userMenuRes.data as any[];
          }
        } catch {
          // Fallback to getModulesList
        }

        if (modulesData.length === 0) {
          const res = await getModulesList();
          if (res && res.success && Array.isArray(res.data)) {
            modulesData = res.data;
          }
        }

        if (isMounted) {
          const transformed = transformApiModules(modulesData);
          // Filter items based on user permissions
          const filtered = transformed.filter((item) => {
            if ((item as any).permissionKey) {
              return hasPermission((item as any).permissionKey, userPermissions, userRoles);
            }
            return true;
          });
          setMenuItems(filtered);
        }
      } catch (err: any) {
        console.error('Error fetching modules list:', err);
        if (isMounted) {
          setError(err?.message || 'Failed to load modules');
          setMenuItems([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchModules();

    return () => {
      isMounted = false;
    };
  }, [userPermissions, userRoles]);

  // Sync open sub-menu dynamically based on API-driven menuItems
  useEffect(() => {
    if (!menuItems || menuItems.length === 0) return;

    const matchedParent = menuItems.find(item =>
      item.subMenu && item.subMenu.length > 0 && item.subMenu.some(sub => 
        currentPath === sub.path || (sub.path !== '/' && currentPath.startsWith(sub.path))
      )
    ) || menuItems.find(item =>
      item.subMenu && item.subMenu.length > 0 && item.path && item.path !== '/' && currentPath.startsWith(item.path)
    );

    if (matchedParent) {
      setOpenSubMenu(matchedParent.name);
    }
  }, [currentPath, menuItems]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>, item: MenuItem) => {
    if (!isCollapsed) return;
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredItem(item);
    setHoverRect(rect);
  };

  const handleMouseLeave = () => {
    if (!isCollapsed) return;
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredItem(null);
      setHoverRect(null);
    }, 150);
  };

  const handlePopupMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopupMouseLeave = () => {
    setHoveredItem(null);
    setHoverRect(null);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Filtering menu items based on search query
  const filteredMenuItems = menuItems.filter(item => {
    const matchesItem = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubMenu = item.subMenu?.some(sub => sub.name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesItem || matchesSubMenu;
  });

  return (
    <div className={`go_erp-bg text-white flex flex-col h-full shrink-0 border-r border-[#B71C1C] overflow-y-auto select-none hide-scrollbar transition-all duration-300 ease-in-out relative ${isCollapsed ? 'w-[75px]' : 'w-[260px]'
      }`}>

      {/* Brand Header */}
      {isCollapsed ? (
        <div className="flex flex-col items-center py-6 border-b border-[#B71C1C]/60 gap-4">
          <div className="bg-white rounded-2xl p-2.5 flex flex-col items-center gap-3 shadow-md border border-[#B71C1C]/20 w-14 transition-all duration-350">
            {/* Crown Icon */}
            <svg className="w-8 h-8 text-[#D4AF37] fill-current" viewBox="0 0 24 24">
              <path d="M12 2l3 6 7-2-4 9H6L2 6l7 2zM5 19h14v2H5z" />
            </svg>
            <div className="w-full border-t border-slate-100 my-0.5"></div>
            {/* Toggle Arrow (Indent Right) */}
            <button
              onClick={onToggleCollapse}
              className="go_erp-text hover:scale-110 active:scale-90 transition-all duration-150 cursor-pointer flex items-center justify-center p-1 rounded-md hover:bg-slate-50"
              title="Expand Sidebar"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h10m0 0l3-3m-3 3l3 3M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-6 border-b border-[#B71C1C]/60">
          <div className="flex items-center">
            <img src={logoImg} alt="Gowardhan" className="h-12 w-auto object-contain animate-fadeIn" />
          </div>
          {/* Toggle Arrow (Indent Left) */}
          <button
            onClick={onToggleCollapse}
            className="text-white/80 hover:text-white hover:scale-105 transition-all duration-150 cursor-pointer flex items-center justify-center p-1.5 rounded-lg bg-[#B71C1C]/40 border border-white/10/70"
            title="Collapse Sidebar"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16M14 6l-4 6 4 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="px-3 py-4 flex justify-center">
        {isCollapsed ? (
          <button
            onClick={onToggleCollapse}
            className="w-12 h-12 rounded-xl bg-[#B71C1C]/40 border border-white hover:border-white/80 flex items-center justify-center text-white cursor-pointer transition-all duration-150"
            title="Search (Expand Sidebar)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        ) : (
          <div className="relative w-full animate-fadeIn">
            <input
              type="text"
              placeholder="Search everything"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#B71C1C]/40 border border-white focus:border-white outline-none text-white placeholder-white text-xs rounded-lg py-2.5 pl-9 pr-4 transition-all duration-155"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white pointer-events-none">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
          </div>
        )}
      </div>

      {/* Navigation List */}
      <nav className="flex-1 px-3 space-y-1 pb-6">
        {loading ? (
          // Skeleton Loading State
          <div className="space-y-2 py-2">
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <div key={idx} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 animate-pulse">
                <div className="w-5 h-5 bg-white/20 rounded-md shrink-0"></div>
                {!isCollapsed && <div className="h-4 bg-white/20 rounded w-24"></div>}
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="px-3 py-6 text-center text-xs text-white/70">
            {error}
          </div>
        ) : filteredMenuItems.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-white/70">
            No modules found
          </div>
        ) : (
          filteredMenuItems.map((item) => {
            const hasSubMenu = Array.isArray(item.subMenu) && item.subMenu.length > 0;
            const isSubMenuSelected = hasSubMenu && item.subMenu?.some(sub => sub.path === currentPath);
            const isParentSelected = item.path && currentPath === item.path && !isSubMenuSelected;
            const isSelected = isParentSelected || isSubMenuSelected;

            const isControlPanelModule = item.path === '/control-panel' || item.path === '/control_panel';

            if (isCollapsed) {
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    if (isControlPanelModule && hasSubMenu && item.subMenu && item.subMenu.length > 0) {
                      navigate(item.subMenu[0].path);
                      onToggleCollapse();
                    } else if (item.path) {
                      navigate(item.path);
                    }
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, item)}
                  onMouseLeave={handleMouseLeave}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-all duration-150 cursor-pointer ${isSelected
                    ? 'go_erp-bg text-white shadow-inner border-l-2 border-white/50 scale-105'
                    : 'text-white/80 hover:text-white'
                    }`}
                >
                  <span className="text-white/85">{item.icon}</span>
                </button>
              );
            }

            if (hasSubMenu) {
              const isOpen = openSubMenu === item.name;
              return (
                <div key={item.name} className={`space-y-1 animate-fadeIn transition-all duration-200 rounded-xl ${isOpen ? 'bg-[#960101] p-2' : ''
                  }`}>
                  <button
                    onClick={() => {
                      setOpenSubMenu(isOpen ? null : item.name);
                      if (isControlPanelModule && hasSubMenu && item.subMenu && item.subMenu.length > 0) {
                        navigate(item.subMenu[0].path);
                      } else if (item.path) {
                        navigate(item.path);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 group cursor-pointer ${isParentSelected
                        ? 'go_erp-bg text-white'
                        : isOpen ? 'hover:bg-white/10' : 'text-white/80 hover:text-white'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={isParentSelected ? 'text-white' : 'text-white/80 group-hover:text-white'}>{item.icon}</span>
                      <span className="tracking-wide text-[13px]">{item.name}</span>
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${isParentSelected ? 'text-white' : 'text-white/70'
                        } ${isOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="pl-6 space-y-1 animate-fadeIn">
                      {item.subMenu?.map((sub, index) => {
                        const isSubSelected = currentPath === sub.path || (isControlPanelModule && item.path && currentPath === item.path && index === 0);
                        return (
                          <button
                            key={sub.name}
                            onClick={() => navigate(sub.path)}
                            className={`w-full text-left px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wider transition-all duration-150 cursor-pointer ${isSubSelected
                              ? 'go_erp-bg text-white shadow-inner font-bold'
                              : 'text-white/75 hover:bg-white/5 hover:text-white'
                              }`}
                          >
                            {sub.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={item.name}
                onClick={() => {
                  setOpenSubMenu(null);
                  if (item.path) {
                    navigate(item.path);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer ${isSelected
                  ? 'go_erp-bg-second text-white'
                  : 'text-white/80 hover:text-white'
                  }`}
              >
                <span className="text-white/85">{item.icon}</span>
                <span className="tracking-wide text-[13px]">{item.name}</span>
              </button>
            );
          })
        )}
      </nav>

      {/* Floating Submenu / Tooltip Popover */}
      {isCollapsed && hoveredItem && hoverRect && (
        <div
          className="fixed z-50 go_erp-bg text-white border border-[#B71C1C] rounded-xl shadow-2xl p-3 flex flex-col gap-1.5 animate-fadeIn min-w-[160px]"
          style={{
            top: hoverRect.top + (hoverRect.height - 48) / 2,
            left: hoverRect.right + 10,
          }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          <div className="text-xs font-bold border-b border-[#B71C1C]/60 pb-1 text-white/90">
            {hoveredItem.name}
          </div>
          {hoveredItem.subMenu && hoveredItem.subMenu.length > 0 ? (
            <div className="flex flex-col gap-1">
              {hoveredItem.subMenu.map((sub) => {
                const isSubSelected = currentPath === sub.path;
                return (
                  <button
                    key={sub.name}
                    onClick={() => {
                      navigate(sub.path);
                      handlePopupMouseLeave();
                    }}
                    className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-all duration-150 cursor-pointer ${isSubSelected
                      ? 'go_erp-bg text-white font-bold'
                      : 'text-[#ECECF2]/80 hover:text-white'
                      }`}
                  >
                    {sub.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-[10px] text-[#ECECF2]/60 select-none">
              Click to navigate
            </div>
          )}
        </div>
      )}

    </div>
  );
};
